'use client'

// Открытое приложение после деплоя. Установленный Turkido на телефоне висит
// в фоне часами со старой страницей, пока его не смахнуть. При возврате из фона
// (и раз в 10 минут, пока открыт) сверяем номер сборки с /api/version; если
// вышла новая - перезагружаем сразу после долгой паузы, иначе на следующем
// переходе по ссылке делаем полную загрузку вместо клиентской (lib/app-version.ts).
import { useEffect } from 'react'
import { BUILD_ID, CHECK_THROTTLE_MS, staleAction } from '@/lib/app-version'

const PERIODIC_MS = 10 * 60 * 1000

export function VersionWatch() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || !BUILD_ID) return
    let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : 0
    let lastCheck = Date.now()
    let stale = false

    async function check(awayMs: number) {
      if (stale) return
      lastCheck = Date.now()
      let server: string | null = null
      try {
        const res = await fetch('/api/version', { cache: 'no-store' })
        if (res.ok) server = ((await res.json()) as { build?: string }).build ?? null
      } catch {
        return
      }
      const action = staleAction(BUILD_ID, server, awayMs)
      if (action === 'reload') window.location.reload()
      else if (action === 'on-next-link') stale = true
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }
      const awayMs = hiddenAt ? Date.now() - hiddenAt : 0
      hiddenAt = 0
      if (Date.now() - lastCheck >= CHECK_THROTTLE_MS || awayMs >= CHECK_THROTTLE_MS) void check(awayMs)
    }

    // Раньше обработчика next/link (фаза захвата): обычный переход по своей
    // ссылке превращаем в полную загрузку страницы уже новой версии.
    const onClick = (e: MouseEvent) => {
      if (!stale || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return
      const url = new URL(a.href, window.location.href)
      if (url.origin !== window.location.origin) return
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return
      e.preventDefault()
      e.stopPropagation()
      window.location.assign(url.href)
    }

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void check(0)
    }, PERIODIC_MS)
    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('click', onClick, true)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('click', onClick, true)
    }
  }, [])

  return null
}
